// The Prepare & Send toolbar's pure parts (mockup:
// docs/design/esign-prepare-and-send-toolbar-mockup-v1.html, approved
// 2026-10-14). Which tool is "armed", what the hint line says, and how a
// person's initials are drawn on the "Placing for" chip live here so they can
// be tested without drawing the 10,000-line screen.
//
// DECISION: the toolbar is a new HOME for controls the panel already had
// (the field palette, "Record field", "Locked text", the other field types).
// It adds no field types and changes nothing about how a field is saved or
// sent. Strike (PR 4a) is the exception in behaviour but not in type: a
// sender-owned text box that prints a line, per send, never saved into a
// layout. Money is not built (it is not a field type either; the database
// CHECK on migration 20260824230106 names the nine that exist).

import type { EsignFieldType } from "../../../../shared/esign/field-types";

/** The one-click tools, in the order the drawing shows them. */
export const TOOLBAR_MAIN_TYPES = [
  "signature",
  "initials",
  "date_signed",
  "text",
  "checkbox",
] as const satisfies readonly EsignFieldType[];

/** Everything else the old panel offered, folded under "More". */
export const TOOLBAR_MORE_TYPES = [
  "dropdown",
  "radio",
  "editable_date",
  "attachment",
] as const satisfies readonly EsignFieldType[];

/** What is armed right now, as a toolbar button id, or null. A bound text
 *  field ("From record") and a locked text field are their own tools even
 *  though both are `text` underneath, because they answer a different
 *  question ("what fills this box?"). */
export type ToolId = EsignFieldType | "locked_text" | "locked_checkbox" | "record" | "strike";

export interface PlacementState {
  placingType: EsignFieldType | null;
  placingLocked: boolean;
  /** A locked box that draws a line through printed text (PR 4a). */
  placingStrike?: boolean;
  pendingMergeToken: string | null;
}

export function armedToolId(s: PlacementState): ToolId | null {
  if (!s.placingType) return null;
  if (s.placingType === "text" && s.placingLocked && s.placingStrike) return "strike";
  if (s.placingType === "text" && s.placingLocked) return "locked_text";
  if (s.placingType === "checkbox" && s.placingLocked) return "locked_checkbox";
  if (s.placingType === "text" && s.pendingMergeToken) return "record";
  return s.placingType;
}

/** Two letters for the avatar on the "Placing for" chip: first and last
 *  word's first letter, "GB" for Grant Botma, one letter for one word. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  const first = words[0]![0]!;
  const last = words.length > 1 ? words[words.length - 1]![0]! : "";
  return (first + last).toUpperCase();
}

/** What a page says about itself under the page: how many blanks on it are
 *  still the sender's to answer (Grant, 2026-10-14: "Page 2 of 4 · 2 blanks
 *  need you"). Zero says so plainly rather than saying nothing, so a long
 *  form reads as a checklist rather than a scroll. */
export function pageNeedsText(count: number): string {
  if (count <= 0) return "Nothing needed";
  return count === 1 ? "1 blank needs you" : `${count} blanks need you`;
}

/** The one line shown while a tool is armed. Null when nothing is. */
export function armedHint(
  armed: ToolId | null,
  signerName: string | null,
  recordLabel: string | null,
  mapping = false,
): string | null {
  if (!armed) return null;
  if (armed === "record") {
    return `Click the page to place. It prints ${recordLabel ?? "that field"} from this record. Esc when done.`;
  }
  if (armed === "strike") {
    return "Click a line of printed text to strike it. Drag the ends to fit. Esc when done.";
  }
  if (armed === "locked_checkbox") {
    return "Click a printed box to check it for the signer. It prints an X they cannot change. Esc when done.";
  }
  if (armed === "locked_text" && mapping) {
    return "Click the page to place fixed text, then type in it. It prints on every future send of this form. Esc when done.";
  }
  if (armed === "locked_text") {
    return "Click the page to place a box only you can fill. Esc when done.";
  }
  return `Click the page to place. It goes to ${signerName ?? "the highlighted signer"}. Esc when done.`;
}
